import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Config from '@app/dev/config/main';
import { t } from '@common/helpers/translator';
import IdleService from '@common/services/idle';
import { POPUPS } from '@common/constants/popups';
import { PopupContext } from '@app/dev/contexts/popup';
import { swarmRequest } from '@common/helpers/util/requestMiddleware';

import { Logout } from '../../../actions/login';
import { makeLogin } from '@app/dev/helpers/login';
import NumericInput from '@common/components/numericInput';

import './style.scss';

const LoginByPinCode = ({ rfid }) => {
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const dispatch = useDispatch();
  const [{ openPopup, closePopup }] = useContext(PopupContext);
  const authData = JSON.parse(window.sessionStorage.getItem('authData'));
  const [form, setForm] = useState({
    pin: '',
    card_number: rfid,
    cashdesk: authData.tid,
  });

  const isFormValid = useMemo(() => form.pin && form.pin.length === 4, [form]);
  const submitDisabled = useMemo(() => !isFormValid || !rfid, [isFormValid, rfid]);

  const fieldChange = (name, value) => {
    if (value.length <= 4) {
      formErrorMsg && setFormErrorMsg('');
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const openReset = () => openPopup(POPUPS.RESET_PIN_CODE, { rfid });

  const submit = async () => {
    if (submitDisabled) return;

    try {
      const data = await swarmRequest(form, 'rfid_login');

      if (data.details === 'InvalidPinCode') return setFormErrorMsg('Invalid PIN code');

      makeLogin({ authData: data, rfid: form.card_number, loggedInByRFID: true });

      if (Config.rfid.logoutIfIdleSecondsCount) {
        IdleService.ifInactive(Config.rfid.logoutIfIdleSecondsCount, () => {
          dispatch(Logout(true));
          closePopup();
        });
      }

      if (form.pin === '0000') {
        openPopup(POPUPS.SET_PIN_CODE, { rfid, loggedIn: true, loginPin: form.pin });
      } else {
        closePopup();
      }
    } catch (e) {
      setForm({ ...form, pin: '' });
      setFormErrorMsg(e.data?.details);
    }
  };

  return (
    <div className="popup-content popup-small">
      <div className="popup-title">{t('PIN Code')}</div>
      <div className="popup-main-content">
        <div className="input-wrapper">
          <p className="popup-txt">{t('Password')}</p>
          <div className="input-row">
            <NumericInput
              type="password"
              onChange={(value) => fieldChange('pin', value)}
              value={form.pin}
              className="numeric-input"
              disableSearchIcon
            />
          </div>
          {formErrorMsg && (
            <div className="error-row" style={{ display: '' }}>
              <i className="global-icon icon-toast-error error-icon-cont" />
              <p className="error-row-text">{t(formErrorMsg)}</p>
            </div>
          )}
        </div>
      </div>
      <div className="popup-actions">
        <button
          type="button"
          onClick={closePopup}
          className="popup-cancel-button"
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          onClick={submit}
          className="popup-confirm-button"
          disabled={submitDisabled}
        >
          {t('Submit')}
        </button>
        <button
          type="button"
          onClick={openReset}
          className="popup-confirm-button reset"
        >
          {t('Reset PIN Code')}
        </button>
      </div>
    </div>
  );
};

LoginByPinCode.propTypes = {
  data: PropTypes.object,
  terminalBalance: PropTypes.number,
  isCardRegistered: PropTypes.number,
  cardRegistered: PropTypes.bool,
};

export default LoginByPinCode;
